import {
  Fragment,
  useState,
  useMemo,
  CSSProperties,
  ReactNode,
  HTMLInputTypeAttribute,
} from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'
import {
  Checkbox,
  Popup,
  type StrictDropdownProps,
  type StrictSearchProps,
} from 'semantic-ui-react'
import { Field as FinalFormField } from 'react-final-form'
import { type FieldValidator } from 'final-form'
import { v4 as uuid } from 'uuid'
import { isEmpty, isPlainObject, isNil } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import Slider from 'rc-slider'

import type { FileUploadProps } from '@/ui/FileUpload'
import Help from '@/ui/Help'
import Form from '@/ui/Form'
import FileUpload from '@/ui/FileUpload'
import Text from '@/ui/Text'
import DepartmentsDropdown from '@/ui/Departments/DepartmentsDropdown'
import { Position } from '@/types/position'
import { DropdownOption, RadioButtonOption } from '@/types'
import FieldEditor from './Editor'
import FieldInput from './Input'
import FieldQuery, { FieldQueryType } from './Query'
import FieldDefault from './Default'
import IntegrationMarkings from './IntegrationMarkings'
import FieldRadio from './RadioButtons'
import FieldColor from './Color'
import InputWithSelect from './InputWithSelect'
import type { InputField as TranslatedEntityField } from '@/components/TranslatedEntityField'
import { IntervalType } from '@/ui/IntervalField'

interface LabelContentProps {
  label?: string | null
  id?: string
  required?: boolean
  help?: ReactNode
  integrationMarkings?: Array<{
    dataDirection: string
    logo: string
    id: string
    name: string
    isZenegy: boolean
  }>
  helpPosition?: Position
  isCheckbox?: boolean
  variables?: any
  error?: string
  style?: CSSProperties
}

export interface FieldProps {
  name: string
  style?: CSSProperties
  label?: string | null
  icon?: string
  croppable?: boolean
  customPlaceholder?: ReactNode
  type?: HTMLInputTypeAttribute
  required?: boolean
  help?: ReactNode
  helpPosition?: Position
  width?: number
  variables?: any
  as?: any
  fieldName?: string
  fieldRequired?: boolean
  fieldDisabled?: boolean
  fieldInitialValues?: string[]
  fieldOnChange?: (data: any) => void
  noMargin?: boolean
  disabledDepartments?: string[] //See DepartmentsDropown (New)
  customFroalaConfig?: any
  excludeToolbarButtons?: string[]
  defaultFileName?: string
  id?: string
  integrationMarkings?: Array<{
    dataDirection: string
    logo: string
    id: string
    name: string
    isZenegy: boolean
  }>
  tooltip?: ReactNode
  query?: FieldQueryType
  options?: DropdownOption[] | RadioButtonOption[]
  placeholder?: string
  scrollSelector?: string
  multi?: boolean
  multiple?: boolean
  fluid?: boolean
  search?: boolean
  selection?: boolean
  toggle?: boolean
  autoFocus?: boolean
  submitId?: boolean
  useTimePicker?: boolean
  hideSearchButton?: boolean
  sortable?: boolean
  loading?: boolean
  hideClearIcon?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  disableToggling?: boolean
  keepTimezone?: boolean
  allowNegative?: boolean
  upward?: boolean
  clearable?: boolean
  saveOnCheck?: boolean
  sensitive?: boolean
  useProportionalColumnSizes?: any
  maxLength?: number
  maxFileSize?: number
  fixedDecimalScale?: boolean
  margin?: any
  allowExclude?: any
  initialExcluded?: any
  initial?: any
  select?: any
  placeholders?: any
  showTags?: boolean
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete']
  decimalScale?: number
  initialValues?: any
  validate?: FieldValidator<any>
  min?: string | number
  max?: string | number
  minDate?: string | Date
  step?: number
  pattern?: string
  startDate?: string | Date | null
  endDate?: string | Date | null
  toolbarOptions?: string[]
  additionLabel?: ReactNode
  additionPosition?: 'bottom'
  allowAdditions?: boolean
  accept?: string
  marks?: Record<number, ''>
  handle?: any
  results?: StrictSearchProps['results']
  onAddItem?: StrictDropdownProps['onAddItem']
  onBlur?: (value?: any, excluded?: any) => void
  onChange?: (e: any, value?: any) => void
  onClick?: (e: any, value?: any) => void
  onSelectFile?: FileUploadProps['onSelectFile']
  onSearchChange?:
    | StrictSearchProps['onSearchChange']
    | ((query: string) => void)
  onResultSelect?: StrictSearchProps['onResultSelect']
  onDatesChange?: ({
    startDate,
    endDate,
  }: {
    startDate?: string | Date
    endDate?: string | Date
  }) => void
  values?: any
  defaultValue?: any
  onClear?: () => void
  onDatesSubmit?: (startDate: any, endDate: any) => void
  onOpen?: () => void
  test?: boolean
  fields?: TranslatedEntityField[]
  alwaysToggled?: boolean
  availableIntervalTypes?: IntervalType[]
}

interface RenderFieldProps extends Omit<FieldProps, 'tooltip'> {
  component: any
  input: any
  meta: any
}

const GlobalStyle = createGlobalStyle`
  .checkbox {
    display: inline-flex !important;
    flex-direction: row-reverse !important;
    justify-content: flex-end !important;

    .checkbox {
      margin-right: 0.5rem !important;

      &:not(.toggle) {
      label:before {
        /* Increase visibility of border */
        border: 1px solid #a1a5b0 !important;
      }
    }
    }

    label {
      cursor: pointer;
    }
  }

  label .icon.help {
    height: 16px;
    display: flex;
    align-items: center;
  }

  .required-dot {
    color: #DE5447 !important;
  }

  .field.error {
    line-height: 0;
  }


  .field .field {
    margin-bottom: 0 !important;
  }

  .validate-icons-container {
    textarea, input[type="text"], .react-date-picker__wrapper {
      padding-right: 30px !important;
    }

    &&.valid {
      textarea, .selection.dropdown, .field .input input, .react-date-picker__wrapper {
        border-color: rgb(100,200,0) !important;
        background-color: rgba(4,125,15, 0.3) !important;
      }
    }
    &&.invalid {
      textarea, .selection.dropdown, .field .input input, .react-date-picker__wrapper {
        border-color: rgb(255,60,60) !important;
        background-color:  rgba(255,0,0, 0.2) !important;
      }
    }
    .ui.selection.dropdown>.dropdown.icon {
      right: 2.6em;
    }
  }

  ${({ theme }) => theme.mediaQueries.mobile} {
    body .ui.form .fields>.field  {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }
`

export const FlexLabel = styled(({ error, ...rest }) => <label {...rest} />)`
  ${({ error }) => css`
    display: flex !important;
    align-items: center;
    width: 100%;
    justify-content: start;
    font-size: 14px !important;
    color: ${error ? '#de5447' : '#012d3e'} !important;
    font-weight: ${error ? '500' : '400'} !important;

    .required-dot {
      color: #de5447;
    }
  `}
`

export const HelpWrapper = styled.div`
  max-height: 300px;
  overflow: auto;
`

const renderWithTooltip = (Component: ReactNode, tooltip?: ReactNode) => {
  if (!tooltip) {
    return Component
  }

  return (
    <Popup
      inverted
      hideOnScroll
      offset={[10, 0]}
      position="right center"
      trigger={<Form.Field>{Component}</Form.Field>}
      content={tooltip}
    />
  )
}

export const LabelContent = ({
  id,
  label,
  required,
  help,
  helpPosition,
  integrationMarkings,
  variables,
  isCheckbox,
  error,
  style,
}: LabelContentProps) => {
  const { t } = useTranslation()

  const helpContent: ReactNode = useMemo(() => {
    const helpText =
      !help && variables
        ? t('Insert # to use variables. (Must follow a space)')
        : help

    if (!helpText) {
      return null
    }

    return <HelpWrapper>{helpText}</HelpWrapper>
  }, [help, variables])

  return (
    <FlexLabel htmlFor={id} style={style} error={error} title={label ?? ''}>
      <Text.Span grey>{label ?? ''}</Text.Span>
      {required && <span className="required-dot">*</span>}
      {!!helpContent && label && (
        <Help content={helpContent} position={helpPosition} />
      )}
      {integrationMarkings && integrationMarkings.length > 0 && !isCheckbox && (
        <IntegrationMarkings integrationMarkings={integrationMarkings} />
      )}
    </FlexLabel>
  )
}

function RenderField(props: RenderFieldProps) {
  const {
    style,
    input,
    label = '',
    icon,
    required,
    help,
    helpPosition,
    width,
    variables, // only used for <FroalaEditorComponent />
    meta: { touched, error },
    as: As = Form.Input,
    customFroalaConfig,
    integrationMarkings,
    defaultFileName,
    ...rest
  } = props

  const [id] = useState(`field-${rest.id || uuid()}`)

  const isCheckbox = As === Form.Checkbox || As === Checkbox

  const sharedProps = {
    ...rest,
    label: (
      <LabelContent
        id={id}
        label={label}
        required={required}
        isCheckbox={isCheckbox}
        integrationMarkings={integrationMarkings}
        help={help}
        helpPosition={helpPosition}
        variables={variables}
        error={touched && error ? error : undefined}
      />
    ),
    labelText: label,
    required,
    input,
    error,
    touched,
    style,
    id,
    icon,
    onBlur: (...args: any) => {
      if (rest.onBlur && typeof rest.onBlur === 'function') {
        rest.onBlur(...args)
      }

      input.onBlur(...args)
    },
  }

  const getValue = () => {
    if (isCheckbox && typeof input.value === 'string') {
      return input.value === 'true'
    }
    if (As === Slider && (isNil(input.value) || input.value === '')) {
      return null
    }

    if (typeof input.value === 'boolean') {
      return 1
    }
    return input.value || (input.multiple ? [] : '')
  }

  const defaultProps = {
    ...sharedProps,
    onChange: (e: Event, semanticValues: any = {}) => {
      const {
        value,
        checked,
        rating,
        maxRating,
        activeColor,
        inactiveColor,
        icon,
      } = semanticValues

      let newValue

      if (rating) {
        newValue = {
          icon,
          rating,
          maxRating,
          activeColor,
          inactiveColor,
        }
      } else if (!isEmpty(value) || Number.isInteger(value)) {
        newValue = value
      } else {
        newValue = e
      }

      input.onChange(isCheckbox ? checked : newValue)
      input.onBlur()

      if (props.onChange) {
        props.onChange(e, { value: newValue, checked })
      }
    },
    value: getValue(),
    fieldError: !!(touched && error),
    isCheckbox,
    As,
    ...(isPlainObject(input.value) ? input.value : {}),
  }

  if (As === 'editor') {
    return (
      <FieldEditor
        customFroalaConfig={customFroalaConfig}
        variables={variables}
        {...sharedProps}
      />
    )
  }

  if (As === 'radio') {
    return <FieldRadio {...sharedProps} />
  }

  if (As === 'inputWithSelect') {
    return <InputWithSelect {...sharedProps} />
  }

  if (As === 'DepartmentsDropdown') {
    return <DepartmentsDropdown {...defaultProps} />
  }
  if (As === Form.Input) {
    return <FieldInput {...sharedProps} />
  }

  if (rest.query) {
    return <FieldQuery {...defaultProps} />
  }
  if (As === 'color') {
    return <FieldColor {...defaultProps} />
  }

  if (As === FileUpload) {
    return <FieldDefault defaultFileName={defaultFileName} {...defaultProps} />
  }

  return (
    <FieldDefault {...defaultProps} integrationMarkings={integrationMarkings} />
  )
}

function Field(props: FieldProps) {
  const { tooltip, ...rest } = props
  return (
    <Fragment>
      <GlobalStyle />
      {renderWithTooltip(
        <FinalFormField {...rest} component={RenderField} />,
        tooltip,
      )}
    </Fragment>
  )
}

export default Field
